'use client';

// The visitor's country, resolved ONCE per request on the server and handed to
// the client tree.
//
// WHY IT COMES FROM THE SERVER. The country has to come from the request IP,
// because Firebase App Hosting injects none of the edge geo headers the
// middleware looks for (x-appengine-country and friends) -- see src/lib/geo-ip.ts.
// That lookup needs `fs` and the MaxMind reader, so it can run in the Node server
// runtime and nowhere else: not in the edge middleware, and obviously not in the
// browser. RootLayout does it and seeds this provider.
//
// WHY NOT AN API ROUTE. The alternative was a client fetch on mount, which would
// make every price on the marketplace flip from "no qualifier" to "plus VAT" a
// beat after paint. A price that visibly changes its terms after the page settles
// is worse than no qualifier at all, and it would land on the largest-contentful
// element of the busiest page. Resolving server-side means the qualifier is in
// the first HTML the buyer sees.
//
// '' means "could not resolve", which every consumer must treat as "show nothing"
// rather than guessing a jurisdiction. See showsVatNotice in src/lib/vat-notice.ts.

import { createContext, useContext, useEffect } from 'react';
import { currencyForCountry, GEO_CURRENCY_COOKIE } from '@/lib/detect-currency';

const GeoCountryContext = createContext<string>('');

/** Days the `gc` cookie lives. Stated in the Privacy Policy cookie table. */
const GEO_COOKIE_MAX_AGE_DAYS = 30;

export function GeoCountryProvider({
  country,
  children,
}: {
  country: string;
  children: React.ReactNode;
}) {
  // Persist the IP-derived CURRENCY as the `gc` cookie.
  //
  // WHY THIS MOVED OUT OF THE MIDDLEWARE. The edge middleware used to write this
  // cookie, and on this deploy target it never could: it resolves the country
  // from GEO_TRUSTED_COUNTRY_HEADERS, that variable is set nowhere, and Firebase
  // App Hosting injects none of the geo headers it names — so resolveCountry()
  // returned null on every request and the cookie was never written. The Privacy
  // Policy has been disclosing it the whole time. The country is only knowable
  // from the bundled MaxMind DB, which needs `fs` and cannot run on the edge, so
  // the write belongs on this side of the boundary: RootLayout resolves it in
  // the Node runtime and hands it down as `country`.
  //
  // Written from the browser rather than as a Set-Cookie header because it is
  // deliberately NOT httpOnly — detectLocaleCurrency() reads it with
  // document.cookie to seed guest currency, so a client write is the same value
  // reaching the same reader, and a Server Component cannot set cookies anyway.
  useEffect(() => {
    const currency = currencyForCountry(country);
    if (!currency) return;
    try {
      // Skip the write when it would not change anything: this effect runs on
      // every navigation that remounts the provider, and re-stamping an
      // identical cookie would also clobber a later manual override.
      const current = document.cookie.match(/(?:^|;\s*)gc=([A-Z]{3})(?:;|$)/)?.[1];
      if (current === currency) return;
      const maxAge = GEO_COOKIE_MAX_AGE_DAYS * 24 * 60 * 60;
      const secure = window.location.protocol === 'https:' ? '; Secure' : '';
      document.cookie =
        `${GEO_CURRENCY_COOKIE}=${currency}; Path=/; Max-Age=${maxAge}; SameSite=Lax${secure}`;
    } catch {
      // Cookies disabled or blocked. detectLocaleCurrency() falls back to the
      // device timezone, which is what happened before this cookie existed.
    }
  }, [country]);

  return (
    <GeoCountryContext.Provider value={country}>
      {children}
    </GeoCountryContext.Provider>
  );
}

/**
 * The visitor's ISO 3166-1 alpha-2 country, or '' when it could not be resolved.
 *
 * Safe to call from any client component: the default is '', so a component
 * rendered outside the provider (a test, a storybook, an error boundary) reads as
 * "unknown" instead of throwing.
 */
export function useGeoCountry(): string {
  return useContext(GeoCountryContext);
}
